import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Switch, notification, Flex, Input, Select, Tooltip } from 'antd';
import type { TableProps, PaginationProps } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getProjectCollection, updateProject } from 'src/api/api';
import ProjectModal from './projectModal';
import { useAppSelector } from 'src/redux/hooks';
import { themeSettings } from 'src/configs/index.tsx';

const currentPageInitialState: API.CurrentPageType = {
  number: 1,
  size: 10,
};

const dimensionInitialState: API.DimensionCollection = {
  items: [],
  total: 0,
  page: 1,
  size: 10,
  pages: 0,
}

const Projects: React.FC = () => {
  const { t } = useTranslation();
  const accessToken = useAppSelector(state => state.auth.accessToken);
  const clientId = useAppSelector(state => state.client.selectedClientData.id);
  const [projects, setProjects] = useState<API.DimensionCollection>(dimensionInitialState);
  const [loading, setLoading] = useState(false);
  const [updatingProject, setUpdatingProject] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState<API.DimensionItem | null>(null);
  const [currentPage, setCurrentPage] = useState<API.CurrentPageType>(
    currentPageInitialState
  );
  const [searchString, setSearchString] = useState<string>('');
  const [orderType, setOrderType] = useState<'ASC' | 'DESC'>('ASC');

  const fetchProjects = async () => {
    setLoading(true);
    try {

      const indexOfLastItem = currentPage.number * currentPage.size;
      const indexOfFirstItem = indexOfLastItem - currentPage.size;

      const params: API.DimensionParams = {
        client_id: clientId,
        limit: currentPage.size,
        offset: indexOfFirstItem,
        search: searchString,
        order_type: orderType,
      }

      const result = await getProjectCollection(accessToken, params);
      setProjects(result.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [accessToken, clientId, currentPage]);

  const openModal = (project?: API.DimensionItem) => {
    setProjectToEdit(project || null);  // Null if creating a new project
    setIsModalOpen(true);
  };

  const switchProjectStatus = async (record: API.DimensionItem) => {
    setUpdatingProject(record.id!);
    try {
      await updateProject(accessToken, { ...record, active: !record.active })
      const msg = record.active
        ? `${record.description} ${' '} ${t('is inactivated.')}`
        : `${record.description} ${' '} ${t('is activated.')}`;

      notification['success']({
        message: t('Success'),
        description: msg,
        placement: 'topRight',
        duration: 4.5,
      });
      
      setProjects((prevProjects: API.DimensionCollection) => {
        const projectItems = prevProjects.items.map((dim) => {
          if (dim.id === record.id) {
            return {
              ...dim,
              active: !record.active,
            };
          }
          return dim;
        });
        return {
          ...prevProjects,
          items: [
            ...projectItems,
          ],
        };
      });
    }
    catch (error) {
      notification['error']({
        message: t('Error'),
        description: t('Could not change status of project'),
        placement: 'topRight',
        duration: 4.5,
      });
      console.error(error);
    }
    finally {
      setUpdatingProject('');
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false);
    setProjectToEdit(null);
  };

  const onPageChange: PaginationProps['onChange'] = (page, pageSize) => {
    setCurrentPage({
      number: page,
      size: pageSize,
    });
  };

  const onShowSizeChange: PaginationProps['onShowSizeChange'] = (
    current,
    pageSize
  ) => {
    setCurrentPage({
      number: current,
      size: pageSize,
    });
  };

  const columns: TableProps<API.DimensionItem>['columns']  = [
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('Code'),
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: t('Active'),
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean, record: API.DimensionItem) => (
        <Switch
          key={record.id + 'isActive'}
          style={{ backgroundColor: active ? '#52c41a' : 'orange' }}
          checkedChildren={t('Activated')}
          unCheckedChildren={t('Inactivated')}
          value={record.active}
          loading={record.id === updatingProject}
          onChange={() => switchProjectStatus(record)}
        />
      ),
    },
    {
      title: t('Action'),
      key: 'action',
      render: (_: any, record: API.DimensionItem) => (
        <Button type="primary" onClick={() => openModal(record)}>
          {t('Edit')}
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      
      <Button type="primary" onClick={() => openModal()}>
        {`${t('Add')} ${t('Project').toLowerCase()} `}
      </Button>

      <Flex wrap gap="small" justify='space-between' >
        <Space>
          <Input
            placeholder={t('Search Code/Description')}
            style={{ width: 300 }}
            onChange={(event) => {
              setSearchString(event.target.value);
            }}
            allowClear
          />
          <Select
            defaultValue="ASC"
            style={{ width: 120 }}
            onChange={(value: 'ASC' | 'DESC') => setOrderType(value)}
            options={[
              { value: 'ASC', label: 'ASC' },
              { value: 'DESC', label: 'DESC' },
            ]}
          />
          <Button type="primary" onClick={() => onPageChange(1, currentPage.size)}>
            {t('Filter Projects')}
          </Button>
        </Space>

        <Tooltip title={t('Refresh')} color={themeSettings.token.colorPrimary} key="refresh-company-cards" >
          <RedoOutlined onClick={fetchProjects} />
        </Tooltip>
      </Flex>

      <Table<API.DimensionItem>
        rowKey="id"
        columns={columns}
        dataSource={projects?.items}
        loading={loading}
        pagination={{
          defaultCurrent: currentPageInitialState.number,
          current: currentPage.number,
          onChange: onPageChange,
          defaultPageSize: currentPageInitialState.size,
          pageSize: currentPage.size,
          onShowSizeChange: onShowSizeChange,
          total: projects?.total ?? 0,
          showSizeChanger: true,
        }}
      />
      <ProjectModal
        open={isModalOpen}
        onClose={handleModalClose}
        project={projectToEdit}
        onSubmit={fetchProjects}  // Refetch projects on submit
      />
    </Space>
  );
};

export default Projects;
